import { supabase } from './supabaseClient'
import { normalizeTimeHHMM } from './trainingSchedule'

export type ProfileStats = {
  height_cm: number | null
  current_weight_kg: number | null
  weekly_commitment: number | null
  meal_frequency: string | null
  weekly_budget_value: number | null
  preferred_currency: string | null
  preferred_workout_start: string | null
  sex: string | null
  last_period_start_date: string | null
  avg_cycle_length: number
  avg_period_duration: number
  exercises_completed: number
  exercises_total: number
  grocery_secured: number
  grocery_total: number
  pending_conflicts: number
  training_streak_days: number
}

export async function loadProfileStats(userId: string): Promise<{ stats: ProfileStats | null; error: string | null }> {
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('height_cm, current_weight_kg, weekly_commitment, meal_frequency, weekly_budget_value, preferred_currency, preferred_workout_start, sex, last_period_start_date, avg_cycle_length, avg_period_duration')
    .eq('id', userId)
    .single()

  if (profileError) return { stats: null, error: profileError.message }

  const { data: schedules } = await supabase.from('training_schedules').select('id').eq('user_id', userId)
  const scheduleIds = (schedules ?? []).map((s) => s.id)

  let exercisesCompleted = 0
  let exercisesTotal = 0

  if (scheduleIds.length > 0) {
    const { data: exercises } = await supabase
      .from('training_block_exercises')
      .select('is_completed')
      .in('schedule_id', scheduleIds)

    exercisesTotal = exercises?.length ?? 0
    exercisesCompleted = exercises?.filter((e) => e.is_completed).length ?? 0
  }

  const { data: grocery } = await supabase.from('grocery_manifest').select('is_secured').eq('user_id', userId)
  const groceryTotal = grocery?.length ?? 0
  const grocerySecured = grocery?.filter((g) => g.is_secured).length ?? 0

  const { count: conflictCount } = await supabase
    .from('calendar_conflict_alerts')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('status', 'pending')

  return {
    stats: {
      height_cm: profile.height_cm ? Number(profile.height_cm) : null,
      current_weight_kg: profile.current_weight_kg ? Number(profile.current_weight_kg) : null,
      weekly_commitment: profile.weekly_commitment,
      meal_frequency: profile.meal_frequency ?? null,
      weekly_budget_value: profile.weekly_budget_value != null ? Number(profile.weekly_budget_value) : null,
      preferred_currency: profile.preferred_currency ?? 'EUR',
      preferred_workout_start: profile.preferred_workout_start
        ? normalizeTimeHHMM(String(profile.preferred_workout_start))
        : null,
      sex: profile.sex ?? null,
      last_period_start_date: profile.last_period_start_date ?? null,
      avg_cycle_length: profile.avg_cycle_length ?? 28,
      avg_period_duration: profile.avg_period_duration ?? 5,
      exercises_completed: exercisesCompleted,
      exercises_total: exercisesTotal,
      grocery_secured: grocerySecured,
      grocery_total: groceryTotal,
      pending_conflicts: conflictCount ?? 0,
      training_streak_days: exercisesCompleted > 0 ? 1 : 0,
    },
    error: null,
  }
}
